// TypeScript types for the bot module.
// Mirrors the Supabase tables created in migration 0010_bot_module.sql.

// Legacy uppercase state list. Used by the UI for filter chips and state
// funnel iteration — we keep this short list to avoid an 18-chip wall while
// bot v2 stays partial. The wider BotState union below accepts both casings.
export const BOT_STATES = [
  'GREETING',
  'AWAITING_PAIN',
  'AWAITING_NAME',
  'IDLE_AFTER_HANDOFF',
  'FAQ_SERVICE',
  'HUMAN_TAKEOVER',
  'DORMANT',
  'CLOSED',
] as const;
export type BotStateLegacy = (typeof BOT_STATES)[number];

export const BOT_STATES_V2 = [
  'greeting',
  'awaiting-pain',
  'awaiting-name',
  'identity-collection',
  'qualification',
  'service-discovery',
  'idle-after-handoff',
  'faq-service',
  'human-takeover',
  'dormant',
  'closed',
] as const;
export type BotStateV2 = (typeof BOT_STATES_V2)[number];

// Mirrors the widened CHECK constraint in migration 0013. Both casings
// coexist during the bot v1 → v2 cutover.
export type BotState = BotStateLegacy | BotStateV2;

export const BOT_STATE_LABELS_HE: Record<BotState, string> = {
  // legacy
  GREETING: 'פתיחה',
  AWAITING_PAIN: 'ממתין לתיאור צורך',
  AWAITING_NAME: 'ממתין לשם',
  IDLE_AFTER_HANDOFF: 'מסירה לבעלים',
  FAQ_SERVICE: 'שאלות נפוצות',
  HUMAN_TAKEOVER: 'בשליטה ידנית',
  DORMANT: 'רדום',
  CLOSED: 'סגור',
  // bot v2
  'greeting': 'פתיחה',
  'awaiting-pain': 'ממתין לתיאור צורך',
  'awaiting-name': 'ממתין לשם',
  'identity-collection': 'איסוף פרטים',
  'qualification': 'הסמכת ליד',
  'service-discovery': 'בירור שירות',
  'idle-after-handoff': 'מסירה לבעלים',
  'faq-service': 'שאלות נפוצות',
  'human-takeover': 'בשליטה ידנית',
  'dormant': 'רדום',
  'closed': 'סגור',
};

// Maps any state (uppercase legacy or lowercase v2) to a canonical "kind" used
// for styling pills and gating action visibility (e.g. takeover/release).
export type BotStateKind =
  | 'greeting'
  | 'awaiting-pain'
  | 'awaiting-name'
  | 'identity-collection'
  | 'qualification'
  | 'service-discovery'
  | 'idle-after-handoff'
  | 'faq-service'
  | 'human-takeover'
  | 'dormant'
  | 'closed';

const STATE_KIND_MAP: Record<BotState, BotStateKind> = {
  GREETING: 'greeting',
  AWAITING_PAIN: 'awaiting-pain',
  AWAITING_NAME: 'awaiting-name',
  IDLE_AFTER_HANDOFF: 'idle-after-handoff',
  FAQ_SERVICE: 'faq-service',
  HUMAN_TAKEOVER: 'human-takeover',
  DORMANT: 'dormant',
  CLOSED: 'closed',
  'greeting': 'greeting',
  'awaiting-pain': 'awaiting-pain',
  'awaiting-name': 'awaiting-name',
  'identity-collection': 'identity-collection',
  'qualification': 'qualification',
  'service-discovery': 'service-discovery',
  'idle-after-handoff': 'idle-after-handoff',
  'faq-service': 'faq-service',
  'human-takeover': 'human-takeover',
  'dormant': 'dormant',
  'closed': 'closed',
};

export function botStateKind(state: BotState): BotStateKind {
  return STATE_KIND_MAP[state];
}

export function displayState(state: BotState): string {
  return BOT_STATE_LABELS_HE[state];
}

export function isHumanTakeoverState(state: BotState): boolean {
  return botStateKind(state) === 'human-takeover';
}

export const SERVICE_SLUGS = [
  'project-management',
  'ai-web',
  'systems',
  'automations',
  'consulting',
] as const;
export type ServiceSlug = (typeof SERVICE_SLUGS)[number];

export const SERVICE_LABELS_HE: Record<ServiceSlug, string> = {
  'project-management': 'ניהול פרויקטים',
  'ai-web': 'אתרים עם AI',
  'systems': 'מערכות פנימיות',
  'automations': 'אוטומציות',
  'consulting': 'ייעוץ עסקי',
};

export const MESSAGE_DIRECTIONS = ['inbound', 'outbound', 'system'] as const;
export type MessageDirection = (typeof MESSAGE_DIRECTIONS)[number];

export const MESSAGE_AUTHORS = ['user', 'bot', 'yoav', 'system'] as const;
export type MessageAuthor = (typeof MESSAGE_AUTHORS)[number];

export const TEMPLATE_CATEGORIES = ['greeting', 'handoff', 'faq', 'price', 'urgent', 'followup'] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

export const TEMPLATE_CATEGORY_LABELS_HE: Record<TemplateCategory, string> = {
  greeting: 'פתיחה',
  handoff: 'מסירה',
  faq: 'שאלות נפוצות',
  price: 'תמחור',
  urgent: 'דחוף',
  followup: 'מעקב',
};

export const BLOCK_REASONS = ['stop_keyword', 'manual', 'spam'] as const;
export type BlockReason = (typeof BLOCK_REASONS)[number];

export const BLOCK_REASON_LABELS_HE: Record<BlockReason, string> = {
  stop_keyword: 'STOP אוטומטי',
  manual: 'חסימה ידנית',
  spam: 'ספאם',
};

export interface BotConversationRow {
  id: string;
  organization_id: string;
  phone: string;
  state: BotState;
  service_slug: ServiceSlug | null;
  lead_id: string | null;
  pain_text: string | null;
  full_name: string | null;
  business_type: string | null;
  lead_score: number | null;
  idle_nudged: boolean;
  closed_acked: boolean;
  fallback_count: number;
  last_fallback_at: string | null;
  last_returning_check_at: string | null;
  conversation_summary: string | null;
  last_message_at: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface BotMessageRow {
  id: string;
  conversation_id: string;
  organization_id: string;
  direction: MessageDirection;
  author: MessageAuthor | null;
  body: string;
  state_at_time: string | null;
  is_nudge: boolean;
  is_fallback: boolean;
  created_at: string;
}

export interface BotBlockedPhoneRow {
  id: string;
  organization_id: string;
  phone: string;
  reason: BlockReason | null;
  blocked_at: string;
  blocked_by: string | null;
  notes: string | null;
  updated_at: string;
}

export interface BotMessageTemplateRow {
  id: string;
  organization_id: string;
  slug: string;
  category: TemplateCategory | null;
  label_he: string;
  body_he: string;
  variables_json: Record<string, unknown> | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface BotScoringRuleRow {
  id: string;
  organization_id: string;
  rule_key: string;
  label_he: string;
  description_he: string | null;
  condition_json: Record<string, unknown>;
  weight: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface BotAnalyticsKpis {
  totalConvs7d: number;
  newConvs7d: number;
  reachedHandoff: number;
  handoffRate: number;            // 0-1
  fallbackRate: number;            // 0-1
  avgMessagesPerConv: number;
  serviceMix: { slug: ServiceSlug | null; count: number }[];
  stateFunnel: { state: BotState; count: number }[];
}

export interface ConversationFilters {
  search: string;
  states: BotState[];
  serviceSlugs: (ServiceSlug | null)[];
  showDormant: boolean;
}

// ─── bot_state_transitions (migration 0013) ─────────────────────────────

export const TRANSITION_EVT_TYPES = ['text','button','list','unsupported','system'] as const;
export type TransitionEvtType = (typeof TRANSITION_EVT_TYPES)[number];

export const TRANSITION_OUTCOMES = [
  'reply','silent','silent_human_lock','silent_fallback_cooldown','error',
] as const;
export type TransitionOutcome = (typeof TRANSITION_OUTCOMES)[number];

export interface BotStateTransitionRow {
  id: string;
  organization_id: string;
  conversation_id: string;
  transition_id: string;
  state_before: string | null;
  state_after: string | null;
  evt_type: TransitionEvtType | null;
  input_snippet: string | null;
  outcome: TransitionOutcome | null;
  actions_count: number;
  created_at: string;
}

// ─── analytics (computed client-side from messages + transitions) ───────

export interface BotConversationMetrics {
  message_count: number;
  duration_seconds: number;
  avg_response_seconds: number;
  longest_state: { state: string; seconds: number } | null;
  handoff_reached: boolean;
  handoff_at: string | null;
  first_response_seconds: number | null;
}

// ─── entity-action Edge Function responses ───────────────────────────────
// Lifecycle-scoped lead status union. Wider than crm/leads/leads.schema.ts
// LEAD_STATUSES (which is bot v1's narrower set) — includes 'quoted' and
// 'converted' as terminal states the bot lifecycle card can drive to.

export type LifecycleLeadStatus = 'new' | 'contacted' | 'qualified' | 'quoted' | 'converted' | 'disqualified';

export const LIFECYCLE_LEAD_STATUS_LABELS_HE: Record<LifecycleLeadStatus, string> = {
  new: 'חדש',
  contacted: 'בקשר',
  qualified: 'מוסמך',
  quoted: 'נשלחה הצעה',
  converted: 'הומר',
  disqualified: 'נפסל',
};

export interface LeadActionChangeStatusResponse {
  success: true;
  entity_id: string;
  status: LifecycleLeadStatus;
  mode?: 'noop';
}

export interface LeadActionConvertResponse {
  success: true;
  entity_id: string;
  customer_id: string;
  mode?: 'noop' | 'created';
}

export interface LeadActionAppendNoteResponse {
  success: true;
  entity_id: string;
}

export type LeadActionResponse =
  | LeadActionChangeStatusResponse
  | LeadActionConvertResponse
  | LeadActionAppendNoteResponse;
